import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  X,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  Loader2,
  Globe,
  Key,
  Link2,
  ChevronRight,
  Info,
  Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { PillButton } from '@/components/ios26/IOSComponents';
import { PLATFORMS_CONFIG, getPlatformName, getApiPlatformId } from '@/lib/platformConstants';
import MetaAppConfig from '@/components/ios26/MetaAppConfig';
import BrevoTargetListSelector from '@/components/ios26/BrevoTargetListSelector';

/**
 * P0 UX OVERHAUL: PlatformConfigModal
 * ===================================
 * Modal centralisé pour configurer les connexions aux plateformes sociales.
 * Affiche les informations techniques (Callback URL, Scopes, etc.) et CTAs.
 * 
 * P0 UX OVERHAUL v2: Multi-env support (Preview + Production URLs)
 * 
 * Déclenché par l'événement global `ame:open-platform-modal`
 */

// Production domain for callback URLs
const PRODUCTION_DOMAIN = 'https://ai-marketing-engine.com';

// Detect if we're on preview or production
const getEnvironmentInfo = () => {
  const currentHost = window.location.origin;
  const isPreview = currentHost.includes('preview.emergentagent.com') || currentHost.includes('localhost');
  
  return {
    isPreview,
    previewUrl: currentHost,
    productionUrl: PRODUCTION_DOMAIN
  };
};

// Platform-specific configuration data
const PLATFORM_TECH_DATA = {
  instagram: {
    name: 'Instagram',
    type: 'oauth',
    gradient: 'linear-gradient(135deg, #833AB4, #FD1D1D, #FCB045)',
    prerequisites: [
      'Compte Instagram Business ou Créateur (pas personnel)',
      'Page Facebook liée à votre compte Instagram',
      'Être administrateur de la Page Facebook'
    ],
    callbackUrl: '/auth/callback/instagram',
    scopes: [
      'instagram_basic',
      'instagram_content_publish',
      'pages_show_list',
      'pages_read_engagement'
    ],
    developerPortalUrl: 'https://developers.facebook.com/apps',
    docUrl: 'https://developers.facebook.com/docs/instagram-api/getting-started',
    commonErrors: [
      {
        code: 'OAuthException 190',
        meaning: 'Token expiré ou révoqué',
        fix: 'Reconnectez votre compte Instagram'
      },
      {
        code: 'Erreur 10',
        meaning: 'Permissions insuffisantes',
        fix: 'Vérifiez les permissions dans Facebook Business'
      }
    ]
  },
  facebook: {
    name: 'Facebook',
    type: 'oauth',
    gradient: 'linear-gradient(135deg, #1877F2, #1877F2)',
    prerequisites: [
      'Page Facebook professionnelle (pas un profil personnel)',
      'Être administrateur de la Page'
    ],
    callbackUrl: '/auth/callback/facebook',
    scopes: [
      'pages_manage_posts',
      'pages_read_engagement',
      'pages_show_list'
    ],
    developerPortalUrl: 'https://developers.facebook.com/apps',
    docUrl: 'https://developers.facebook.com/docs/pages-api/getting-started',
    commonErrors: [
      {
        code: 'OAuthException 190',
        meaning: 'Token expiré',
        fix: 'Reconnectez votre compte'
      },
      {
        code: 'Erreur 200',
        meaning: 'Permissions Page insuffisantes',
        fix: 'Vérifiez que vous êtes admin de la Page'
      }
    ]
  },
  // P0 RESTORE: x_twitter supports both default env OAuth AND custom credentials
  x_twitter: {
    name: 'X (Twitter)',
    type: 'oauth_hybrid', // P0 RESTORE: Supports both simple OAuth and custom app
    gradient: 'linear-gradient(135deg, #000000, #14171A)',
    prerequisites: [
      'Compte X pour l\'autorisation',
      'Accepter les permissions de publication (tweet.write requis)'
    ],
    // P0 RESTORE: Fields shown only when "Use my X App" is enabled
    apiFields: [
      { name: 'client_id', label: 'Client ID', placeholder: 'Votre Client ID OAuth 2.0', type: 'text' },
      { name: 'client_secret', label: 'Client Secret (optionnel pour PKCE)', placeholder: 'Client Secret (si confidential)', type: 'password' }
    ],
    developerPortalUrl: 'https://developer.twitter.com/en/portal/dashboard',
    docUrl: 'https://developer.twitter.com/en/docs/twitter-api',
    needsCallback: true,
    supportsCustomApp: true, // P0 RESTORE: Enables "Advanced" toggle
    commonErrors: [
      {
        code: 'Rate Limit 429',
        meaning: 'Trop de requêtes (API Free)',
        fix: 'Attendez quelques minutes avant de réessayer'
      },
      {
        code: 'Forbidden 403',
        meaning: 'Accès refusé',
        fix: 'Vérifiez que vous avez autorisé l\'application'
      },
      {
        code: 'missing_scope_tweet.write',
        meaning: 'Permission tweet.write manquante',
        fix: 'Reconnectez X et acceptez les permissions Read+Write'
      },
      {
        code: 'X_NOT_CONFIGURED',
        meaning: 'OAuth X non configuré',
        fix: 'Ajoutez vos credentials X dans "Avancé" ou contactez l\'administrateur'
      }
    ]
  },
  linkedin: {
    name: 'LinkedIn',
    type: 'oauth',
    gradient: 'linear-gradient(135deg, #0A66C2, #0A66C2)',
    prerequisites: [
      'Compte LinkedIn personnel ou Page entreprise',
      'Pour les Pages: être administrateur'
    ],
    callbackUrl: '/auth/callback/linkedin',
    scopes: [
      'r_liteprofile',
      'w_member_social',
      'r_organization_social (Pages)'
    ],
    developerPortalUrl: 'https://www.linkedin.com/developers/apps',
    docUrl: 'https://learn.microsoft.com/en-us/linkedin/marketing/',
    commonErrors: [
      {
        code: 'Unauthorized 401',
        meaning: 'Token expiré',
        fix: 'Reconnectez votre compte LinkedIn'
      }
    ]
  },
  blog: {
    name: 'Blog / Site web',
    type: 'api_key',
    gradient: 'linear-gradient(135deg, #10B981, #14B8A6)',
    prerequisites: [
      'Blog avec accès API (WordPress, Ghost, Webflow...)',
      'Clé API ou mot de passe d\'application'
    ],
    apiFields: [
      { name: 'api_url', label: 'URL de l\'API', placeholder: 'https://mon-site.com/wp-json/wp/v2' },
      { name: 'username', label: 'Nom d\'utilisateur', placeholder: 'admin' },
      { name: 'app_password', label: 'Mot de passe d\'application', placeholder: 'xxxx xxxx xxxx', type: 'password' }
    ],
    docUrl: 'https://developer.wordpress.org/rest-api/reference/posts/',
    commonErrors: [
      {
        code: 'Erreur 401',
        meaning: 'Authentification échouée',
        fix: 'Vérifiez vos identifiants API'
      }
    ]
  },
  brevo: {
    name: 'Email (Brevo)',
    type: 'api_key',
    gradient: 'linear-gradient(135deg, #0B996E, #0B996E)',
    prerequisites: [
      'Compte Brevo (ex-Sendinblue)',
      'Clé API v3 générée',
      'Email expéditeur vérifié'
    ],
    apiFields: [
      { name: 'api_key', label: 'Clé API Brevo', placeholder: 'xkeysib-...', type: 'password' },
      { name: 'sender_email', label: 'Email expéditeur', placeholder: 'newsletter@votredomaine.com', type: 'email' },
      { name: 'sender_name', label: 'Nom expéditeur', placeholder: 'Mon Entreprise', type: 'text' }
    ],
    developerPortalUrl: 'https://app.brevo.com/settings/keys/api',
    docUrl: 'https://developers.brevo.com/docs',
    commonErrors: [
      {
        code: 'Erreur 401',
        meaning: 'Clé API invalide',
        fix: 'Utilisez une clé API v3 (pas v2)'
      },
      {
        code: 'Emails non envoyés',
        meaning: 'Expéditeur non vérifié',
        fix: 'Configurez et vérifiez votre expéditeur dans Brevo'
      }
    ]
  },
  telegram: {
    name: 'Telegram',
    type: 'telegram_custom', // Custom type for simplified Telegram UI
    gradient: 'linear-gradient(135deg, #0088cc, #229ED9)',
    prerequisites: [
      'Bot Telegram créé via @BotFather',
      'Token du bot (reçu de @BotFather)'
    ],
    apiFields: [
      { name: 'bot_token', label: 'Bot Token', placeholder: '123456789:ABCdefGHIjklMNOpqrsTUVwxyz', type: 'password' },
      { name: 'chat_id', label: 'Chat ID / Canal', placeholder: '@moncanal ou -1001234567890', type: 'text' }
    ],
    developerPortalUrl: 'https://t.me/BotFather',
    docUrl: 'https://core.telegram.org/bots/api',
    commonErrors: [
      {
        code: 'Erreur 401 Unauthorized',
        meaning: 'Token bot invalide',
        fix: 'Vérifiez le token reçu de @BotFather'
      },
      {
        code: 'Erreur 400 Chat not found',
        meaning: 'Canal/groupe introuvable',
        fix: 'Ajoutez le bot comme admin du canal'
      }
    ]
  },
  pinterest: {
    name: 'Pinterest',
    type: 'pinterest_custom', // Custom type for Pinterest with board selection
    gradient: 'linear-gradient(135deg, #E60023, #BD081C)',
    prerequisites: [
      'Compte Pinterest Business ou Créateur',
      'Application Pinterest créée sur le Developer Portal'
    ],
    apiFields: [
      { name: 'client_id', label: 'App ID', placeholder: 'Votre Pinterest App ID', type: 'text' },
      { name: 'client_secret', label: 'App Secret', placeholder: 'Votre Pinterest App Secret', type: 'password' }
    ],
    callbackUrl: '/api/auth/pinterest/callback',
    scopes: [
      'boards:read',
      'boards:write',
      'pins:read',
      'pins:write',
      'user_accounts:read'
    ],
    developerPortalUrl: 'https://developers.pinterest.com/apps/',
    docUrl: 'https://developers.pinterest.com/docs/api/v5/',
    commonErrors: [
      {
        code: 'Erreur 401',
        meaning: 'Token expiré ou invalide',
        fix: 'Reconnectez votre compte Pinterest'
      },
      {
        code: 'BOARD_NOT_FOUND',
        meaning: 'Tableau introuvable',
        fix: 'Sélectionnez un autre tableau ou créez-en un nouveau sur Pinterest'
      },
      {
        code: 'MEDIA_ERROR',
        meaning: 'Problème avec l\'image',
        fix: 'Utilisez une image verticale (1000x1500px) au format PNG ou JPEG'
      }
    ]
  }
};

/**
 * Utility: Copy to clipboard with toast feedback
 */
const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(null);
  
  const copy = async (text, label = 'Texte') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copié !`);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Erreur lors de la copie');
    }
  };
  
  return { copied, copy };
};

/**
 * Pinterest Configuration Section Component
 * Handles OAuth setup and board selection for Pinterest
 */
function PinterestConfigSection({ apiFormData, setApiFormData, projectId, onConnectionChange }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [boards, setBoards] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const { copied, copy } = useCopyToClipboard();
  
  // Get callback URL for Pinterest
  const callbackUrl = `${window.location.origin}/api/auth/pinterest/callback`;
  
  // Load Pinterest status on mount
  useEffect(() => {
    loadPinterestStatus();
  }, []);
  
  const loadPinterestStatus = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/integrations/pinterest/status?_ts=${Date.now()}`);
      setStatus(response.data);
      onConnectionChange?.(response.data.token_valid && response.data.default_board_id);
      
      // If connected, load boards
      if (response.data.token_valid) {
        loadBoards();
      }
    } catch (e) {
      console.error('[Pinterest] Status error:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const loadBoards = async () => {
    setLoadingBoards(true);
    try {
      const response = await apiClient.get(`/social/pinterest/boards?_ts=${Date.now()}`);
      if (response.data.success) {
        setBoards(response.data.boards || []);
      }
    } catch (e) {
      console.error('[Pinterest] Boards error:', e);
      if (e.response?.status === 401) {
        toast.error('Token Pinterest expiré. Veuillez vous reconnecter.');
        setStatus(prev => ({ ...prev, token_valid: false }));
      }
    } finally {
      setLoadingBoards(false);
    }
  };
  
  const handleSaveConfig = async () => {
    if (!apiFormData.client_id || !apiFormData.client_secret) {
      toast.error('Veuillez remplir App ID et App Secret');
      return;
    }
    
    setConfigSaving(true);
    try {
      const response = await apiClient.post('/integrations/pinterest/config', {
        client_id: apiFormData.client_id,
        client_secret: apiFormData.client_secret,
        redirect_uri: callbackUrl
      });
      
      if (response.data.success) {
        toast.success('Configuration Pinterest sauvegardée');
        loadPinterestStatus();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setConfigSaving(false);
    }
  };
  
  const handleConnect = async () => {
    try {
      const response = await apiClient.get(`/auth/pinterest/connect?project_id=${projectId}`);
      if (response.data.auth_url) {
        // Redirect to Pinterest OAuth
        window.location.href = response.data.auth_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de connexion Pinterest');
    }
  };
  
  const handleSelectBoard = async (boardId, boardName) => {
    try {
      const response = await apiClient.post('/social/pinterest/select-board', {
        board_id: boardId,
        board_name: boardName
      });
      
      if (response.data.success) {
        toast.success(`Tableau "${boardName}" sélectionné`);
        setStatus(prev => ({ ...prev, default_board_id: boardId, default_board_name: boardName }));
        onConnectionChange?.(true);
      }
    } catch (e) {
      toast.error('Erreur lors de la sélection du tableau');
    }
  };
  
  const handleTestConnection = async () => {
    setTestLoading(true);
    try {
      console.info('[PINTEREST:UI] test pin request method=POST endpoint=/api/social/pinterest/test');
      const response = await apiClient.post('/social/pinterest/test');
      
      if (response.data.success) {
        toast.success(
          <div>
            <strong>Test réussi !</strong>
            <div className="text-sm mt-1">
              Pin de test créé. <a href={response.data.pin_url} target="_blank" rel="noopener noreferrer" className="underline">Voir le pin</a>
            </div>
          </div>
        );
      } else {
        // P0 FIX: Handle specific Pinterest errors
        const errorMsg = response.data.error || 'Test échoué';
        if (errorMsg.includes('Trial access')) {
          console.info('[PINTEREST:UI] classify_error=trial_access');
          toast.error(
            <div>
              <strong>Limitation Pinterest Trial</strong>
              <div className="text-sm mt-1">
                Le compte est connecté mais votre app Pinterest est en mode Trial. 
                La création de pins en production n'est pas autorisée.
              </div>
            </div>,
            { duration: 8000 }
          );
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (e) {
      // P0 FIX: Handle specific Pinterest errors
      const errorStatus = e.response?.status;
      const errorDetail = e.response?.data?.detail || '';
      
      // Handle 405 Method Not Allowed
      if (errorStatus === 405) {
        console.error('[PINTEREST:UI] classify_error=method_not_allowed status=405');
        toast.error('Erreur technique (405). La connexion du compte est néanmoins active.');
        return;
      }
      
      // Handle Trial access message
      if (errorDetail.includes('Trial access')) {
        console.info('[PINTEREST:UI] classify_error=trial_access');
        toast.error(
          <div>
            <strong>Limitation Pinterest Trial</strong>
            <div className="text-sm mt-1">
              Le compte est connecté mais votre app Pinterest est en mode Trial. 
              La création de pins en production n'est pas autorisée.
            </div>
          </div>,
          { duration: 8000 }
        );
        return;
      }
      
      toast.error(errorDetail || 'Erreur lors du test');
    } finally {
      setTestLoading(false);
    }
  };
  
  const handleDisconnect = async () => {
    try {
      await apiClient.delete('/integrations/pinterest');
      toast.success('Pinterest déconnecté');
      setStatus(null);
      setBoards([]);
      onConnectionChange?.(false);
    } catch (e) {
      toast.error('Erreur lors de la déconnexion');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#E60023]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-5 pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
      
      {/* Step 1: App Configuration */}
      <div className="space-y-4">
        <h4 className="font-medium text-semantic flex items-center gap-2">
          <Key className="w-4 h-4" />
          1. Configuration de l'application Pinterest
        </h4>
        
        {/* Callback URL */}
        <div className="p-3 rounded-xl bg-[#E60023]/5 border border-[#E60023]/10">
          <p className="text-xs font-medium text-[#E60023] mb-2">
            Ajoutez cette URL de Callback dans Pinterest Developer Portal :
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={callbackUrl}
              readOnly
              className="flex-1 input-semantic text-xs font-mono"
            />
            <button
              onClick={() => copy(callbackUrl, 'Callback URL')}
              className="p-2 rounded-lg transition-colors bg-[#E60023]/10 hover:bg-[#E60023]/20"
            >
              {copied === 'Callback URL' ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-[#E60023]" />
              )}
            </button>
          </div>
        </div>
        
        {/* App ID */}
        <div>
          <label className="block text-sm font-medium text-semantic mb-1.5">
            App ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={apiFormData.client_id || ''}
            onChange={(e) => setApiFormData(prev => ({ ...prev, client_id: e.target.value }))}
            placeholder="Votre Pinterest App ID"
            className="w-full input-semantic"
            data-testid="pinterest-app-id"
          />
        </div>
        
        {/* App Secret */}
        <div>
          <label className="block text-sm font-medium text-semantic mb-1.5">
            App Secret <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={apiFormData.client_secret || ''}
            onChange={(e) => setApiFormData(prev => ({ ...prev, client_secret: e.target.value }))}
            placeholder="Votre Pinterest App Secret"
            className="w-full input-semantic"
            data-testid="pinterest-app-secret"
          />
        </div>
        
        {/* Save Config Button */}
        <button
          onClick={handleSaveConfig}
          disabled={configSaving || !apiFormData.client_id || !apiFormData.client_secret}
          className={cn(
            "w-full py-2.5 rounded-xl text-sm font-medium transition-all",
            "bg-[#E60023]/10 text-[#E60023] hover:bg-[#E60023]/20",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "flex items-center justify-center gap-2"
          )}
          data-testid="pinterest-save-config"
        >
          {configSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
          Sauvegarder la configuration
        </button>
      </div>
      
      {/* Step 2: OAuth Connection */}
      {status?.has_credentials && (
        <div className="space-y-4 pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
          <h4 className="font-medium text-semantic flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            2. Connexion OAuth
          </h4>
          
          {status.token_valid ? (
            <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">Connecté</p>
                    <p className="text-sm text-green-600">@{status.username || 'Pinterest User'}</p>
                  </div>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="text-sm text-red-500 hover:underline"
                >
                  Déconnecter
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleConnect}
              className={cn(
                "w-full py-3 rounded-xl text-sm font-medium transition-all",
                "bg-[#E60023] text-white hover:bg-[#BD081C]",
                "flex items-center justify-center gap-2"
              )}
              data-testid="pinterest-connect-oauth"
            >
              <ExternalLink className="w-4 h-4" />
              Connecter avec Pinterest
            </button>
          )}
        </div>
      )}
      
      {/* Step 3: Board Selection */}
      {status?.token_valid && (
        <div className="space-y-4 pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-semantic flex items-center gap-2">
              <Globe className="w-4 h-4" />
              3. Sélection du tableau
            </h4>
            <button
              onClick={loadBoards}
              disabled={loadingBoards}
              className="text-sm text-[#E60023] hover:underline flex items-center gap-1"
            >
              {loadingBoards ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings className="w-3 h-3" />}
              Actualiser
            </button>
          </div>
          
          {status.default_board_id ? (
            <div className="p-3 rounded-xl bg-[#E60023]/5 border border-[#E60023]/20">
              <p className="text-sm">
                <strong>Tableau actuel :</strong> {status.default_board_name || status.default_board_id}
              </p>
            </div>
          ) : (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Aucun tableau sélectionné. Choisissez un tableau pour publier.
              </p>
            </div>
          )}
          
          {/* Board List */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {boards.map(board => (
              <button
                key={board.id}
                onClick={() => handleSelectBoard(board.id, board.name)}
                className={cn(
                  "w-full p-3 rounded-xl text-left transition-all border",
                  board.id === status.default_board_id
                    ? "border-[#E60023] bg-[#E60023]/5"
                    : "border-semantic-border hover:bg-semantic-surface-3"
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-semantic">{board.name}</p>
                    <p className="text-xs text-semantic-muted">{board.pin_count} épingles</p>
                  </div>
                  {board.id === status.default_board_id && (
                    <Check className="w-5 h-5 text-[#E60023]" />
                  )}
                </div>
              </button>
            ))}
            
            {boards.length === 0 && !loadingBoards && (
              <p className="text-sm text-semantic-muted text-center py-4">
                Aucun tableau trouvé. Créez un tableau sur Pinterest.
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Step 4: Test Connection */}
      {status?.token_valid && status?.default_board_id && (
        <div className="pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
          <button
            onClick={handleTestConnection}
            disabled={testLoading}
            className={cn(
              "w-full py-2.5 rounded-xl text-sm font-medium transition-all",
              "bg-green-500/10 text-green-600 hover:bg-green-500/20",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "flex items-center justify-center gap-2"
            )}
            data-testid="pinterest-test-connection"
          >
            {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Tester la connexion (créer un pin test)
          </button>
        </div>
      )}
      
      {/* Help Box */}
      <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
        <p className="text-xs text-semantic-muted">
          <strong className="text-semantic">Configuration Pinterest :</strong>
          <br />
          1. Créez une app sur <a href="https://developers.pinterest.com/apps/" target="_blank" rel="noopener noreferrer" className="text-[#E60023] underline">Pinterest Developer</a>
          <br />
          2. Ajoutez le Callback URL ci-dessus dans OAuth settings
          <br />
          3. Copiez App ID et App Secret ici
          <br />
          4. Connectez-vous et sélectionnez un tableau
        </p>
      </div>
    </div>
  );
}

/**
 * Platform Logo Component
 */
function PlatformLogo({ platformId, className = 'w-6 h-6' }) {
  if (platformId === 'blog') {
    return <Globe className={className} />;
  }
  
  const logos = {
    instagram: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    facebook: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    x: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    brevo: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M3.5 5.5A2.5 2.5 0 016 3h12a2.5 2.5 0 012.5 2.5v1.1L12 11.4 3.5 6.6V5.5zM3.5 8.4v10.1A2.5 2.5 0 006 21h12a2.5 2.5 0 002.5-2.5V8.4l-8 4.5a1 1 0 01-1 0l-8-4.5z"/>
      </svg>
    ),
    telegram: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    pinterest: (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
      </svg>
    )
  };
  
  return logos[platformId] || <Globe className={className} />;
}

/**
 * Main Modal Component
 */
export default function PlatformConfigModal({ platformId, isOpen, onClose, onConnect }) {
  const { projectId } = useParams();
  const { copied, copy } = useCopyToClipboard();
  const [connecting, setConnecting] = useState(false);
  const [apiFormData, setApiFormData] = useState({});
  const [activeSection, setActiveSection] = useState('prerequisites');
  
  // P0 RESTORE: States for X custom app credentials
  const [useCustomXApp, setUseCustomXApp] = useState(false);
  const [xAuthType, setXAuthType] = useState('public_pkce'); // 'public_pkce' or 'confidential'
  const [xConfigLoading, setXConfigLoading] = useState(false);
  const [xConfigStatus, setXConfigStatus] = useState(null); // {has_custom_app, client_id_preview, ...}
  const [xCredentialsRequired, setXCredentialsRequired] = useState(false); // P0 FIX: True if no env default
  
  const platform = PLATFORM_TECH_DATA[platformId];
  
  // P0 UX OVERHAUL v2: Multi-env callback URLs
  const envInfo = useMemo(() => getEnvironmentInfo(), []);
  const callbackUrls = useMemo(() => {
    if (!platform?.callbackUrl) return null;
    return {
      preview: `${envInfo.previewUrl}${platform.callbackUrl}`,
      production: `${envInfo.productionUrl}${platform.callbackUrl}`
    };
  }, [platform, envInfo]);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setApiFormData({});
      setActiveSection('prerequisites');
      
      // Load existing credentials for X (P0 FIX: use x_twitter key)
      if (platformId === 'x_twitter') {
        // P0 RESTORE: Load X config status (custom app info)
        loadXConfigStatus();
      }
    }
  }, [isOpen, platformId]);
  
  // P0 FIX: Load X custom app config status and determine required fields
  const loadXConfigStatus = async () => {
    setXConfigLoading(true);
    try {
      // Get custom app config
      const configResp = await apiClient.get(`/integrations/x/config?project_id=${projectId}&_ts=${Date.now()}`);
      console.log('[XConfigModal] Config status:', configResp.data);
      setXConfigStatus(configResp.data);
      
      // P0 FIX: If custom app is configured, show the fields
      if (configResp.data?.has_custom_app) {
        setUseCustomXApp(true);
        setXAuthType(configResp.data.auth_type || 'public_pkce');
      }
      
      // P0 FIX: If NO env default is available, force custom app mode
      if (!configResp.data?.env_default_available && !configResp.data?.has_custom_app) {
        console.log('[XConfigModal] No env default available - forcing custom app mode');
        setUseCustomXApp(true);
        setXCredentialsRequired(true);
        // Show technical section with inputs visible
        setActiveSection('technical');
      }
      
      // Also check connection status
      const statusResp = await apiClient.get(`/integrations/x/status?project_id=${projectId}&_ts=${Date.now()}`);
      console.log('[XConfigModal] Connection status:', statusResp.data);
      
      if (statusResp.data?.connected) {
        setIsConnected(true);
        
        // Check for missing tweet.write scope
        if (statusResp.data?.missing_scope_tweet_write) {
          toast.warning('Permission tweet.write manquante. Reconnectez pour publier des tweets.');
        }
      }
      
      // P0 FIX: Check if credentials are required but not configured
      if (statusResp.data?.error_code === 'X_NOT_CONFIGURED' || 
          (!configResp.data?.env_default_available && !configResp.data?.has_custom_app)) {
        setXCredentialsRequired(true);
        setUseCustomXApp(true);
        setActiveSection('technical');
      }
      
    } catch (e) {
      console.log('[XConfigModal] Status check error:', e);
      // If error, assume credentials are required
      setXCredentialsRequired(true);
      setUseCustomXApp(true);
    } finally {
      setXConfigLoading(false);
    }
  };
  
  const [disconnecting, setDisconnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  
  // P0 UX: Telegram simplified UI states
  const [telegramTestLoading, setTelegramTestLoading] = useState(false);
  const [telegramTestResult, setTelegramTestResult] = useState(null);
  const [telegramDetectLoading, setTelegramDetectLoading] = useState(false);
  const [telegramDetectedChats, setTelegramDetectedChats] = useState([]);
  const [telegramValidation, setTelegramValidation] = useState({ bot_token: null, chat_id: null });
  
  // Telegram validation regex
  const TELEGRAM_BOT_TOKEN_REGEX = /^\d+:[A-Za-z0-9_-]+$/;
  const TELEGRAM_CHAT_ID_REGEX = /^(@[a-zA-Z0-9_]+|-?\d+)$/;
  
  // Telegram validation function
  const validateTelegramFields = (field, value) => {
    if (field === 'bot_token') {
      if (!value) return null;
      return TELEGRAM_BOT_TOKEN_REGEX.test(value) ? null : 'Format invalide. Ex: 123456789:ABCdef...';
    }
    if (field === 'chat_id') {
      if (!value) return null;
      return TELEGRAM_CHAT_ID_REGEX.test(value) ? null : 'Format invalide. Ex: @channel ou -100123456789';
    }
    return null;
  };
  
  // Telegram test connection
  const handleTelegramTestConnection = async () => {
    const botToken = apiFormData.bot_token;
    if (!botToken) {
      toast.error('Veuillez entrer le Bot Token');
      return;
    }
    
    const tokenError = validateTelegramFields('bot_token', botToken);
    if (tokenError) {
      toast.error(tokenError);
      return;
    }
    
    setTelegramTestLoading(true);
    setTelegramTestResult(null);
    
    try {
      // Test via Telegram API directly (getMe)
      const response = await fetch(`https://api.telegram.org/bot${botToken}/getMe`);
      const data = await response.json();
      
      if (data.ok) {
        setTelegramTestResult({
          success: true,
          bot_username: data.result.username,
          bot_name: data.result.first_name
        });
        toast.success(`Bot @${data.result.username} connecté !`);
      } else {
        setTelegramTestResult({ success: false, error: data.description });
        toast.error(`Token invalide: ${data.description}`);
      }
    } catch (error) {
      setTelegramTestResult({ success: false, error: error.message });
      toast.error('Erreur de connexion');
    } finally {
      setTelegramTestLoading(false);
    }
  };
  
  // Telegram auto-detect chat ID
  const handleTelegramDetectChatId = async () => {
    const botToken = apiFormData.bot_token;
    if (!botToken) {
      toast.error('Veuillez d\'abord entrer et tester le Bot Token');
      return;
    }
    
    setTelegramDetectLoading(true);
    setTelegramDetectedChats([]);
    
    try {
      const response = await fetch(`https://api.telegram.org/bot${botToken}/getUpdates?limit=50`);
      const data = await response.json();
      
      if (data.ok) {
        const chats = [];
        const seenIds = new Set();
        
        for (const update of data.result || []) {
          for (const key of ['message', 'channel_post', 'my_chat_member', 'chat_member']) {
            if (update[key]?.chat) {
              const chat = update[key].chat;
              const chatId = String(chat.id);
              if (!seenIds.has(chatId)) {
                seenIds.add(chatId);
                chats.push({
                  id: chatId,
                  title: chat.title || chat.username || chat.first_name || chatId,
                  type: chat.type
                });
              }
            }
          }
        }
        
        if (chats.length > 0) {
          setTelegramDetectedChats(chats);
          toast.success(`${chats.length} chat(s) détecté(s)`);
        } else {
          toast.info('Aucun chat détecté. Envoyez un message au bot ou ajoutez-le à un canal/groupe.');
        }
      } else {
        toast.error(`Erreur: ${data.description}`);
      }
    } catch (error) {
      toast.error('Erreur lors de la détection');
    } finally {
      setTelegramDetectLoading(false);
    }
  };
  
  // Select detected chat
  const handleSelectDetectedChat = (chat) => {
    setApiFormData(prev => ({ ...prev, chat_id: chat.id }));
    setTelegramDetectedChats([]);
    toast.success(`Chat "${chat.title}" sélectionné`);
  };
  
  // Check if platform is already connected
  useEffect(() => {
    const checkConnection = async () => {
      if (!isOpen || !projectId) return;
      try {
        const response = await apiClient.get(`/projects/${projectId}`);
        const channels = response.data?.channels_config || {};
        const channelConfig = channels[platformId] || {};
        setIsConnected(channelConfig.connected === true || channelConfig.enabled === true);
      } catch (e) {
        setIsConnected(false);
      }
    };
    checkConnection();
  }, [isOpen, projectId, platformId]);
  
  if (!isOpen || !platform) return null;
  
  const handleDisconnect = async () => {
    if (!window.confirm(`Voulez-vous déconnecter ${platform.name} ? Vous pourrez le reconnecter à tout moment.`)) {
      return;
    }
    
    setDisconnecting(true);
    try {
      await apiClient.put(`/projects/${projectId}/channels/${platformId}`, {
        enabled: false,
        connected: false,
        api_key: null
      });
      toast.success(`${platform.name} déconnecté`);
      setIsConnected(false);
      onConnect?.(platformId); // Refresh parent
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setDisconnecting(false);
    }
  };
  
  const handleOAuthConnect = async () => {
    setConnecting(true);
    try {
      // P1 FIX: Facebook uses a different OAuth endpoint - GET /auth/facebook/connect
      // This endpoint returns { auth_url } for browser redirect (NOT oauth_url!)
      const apiPlatform = getApiPlatformId(platformId);
      
      let oauthUrl = null;
      
      if (apiPlatform === 'facebook' || apiPlatform === 'instagram') {
        // Facebook/Instagram: Use GET /auth/{platform}/connect - returns { auth_url }
        const response = await apiClient.get(`/auth/${apiPlatform}/connect`);
        // P1 FIX: Backend returns "auth_url", not "oauth_url"
        oauthUrl = response.data.auth_url;
        console.log('[OAuth] Facebook/Instagram auth_url received:', oauthUrl ? 'YES' : 'NO');
      } else {
        // Other platforms: Use POST /integrations/{platform}/connect - returns { oauth_url }
        const response = await apiClient.post(`/integrations/${apiPlatform}/connect`, {
          project_id: projectId
        });
        oauthUrl = response.data.oauth_url;
        
        if (response.data.config_required) {
          toast.info(response.data.message || 'Configuration requise');
          setConnecting(false);
          return;
        }
      }
      
      if (oauthUrl) {
        toast.info('Redirection vers l\'authentification...');
        // P1 FIX: Actually redirect the browser to Meta OAuth
        window.location.href = oauthUrl;
      } else {
        toast.error('URL OAuth non reçue - vérifiez la configuration Meta App');
      }
    } catch (error) {
      console.error('[OAuth] Error:', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      let msg = 'Erreur de connexion';
      if (typeof detail === 'object' && detail.message) {
        msg = detail.message;
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };
  
  const handleApiKeyConnect = async () => {
    // P0 FIX: Special validation for X (Twitter) - depends on useCustomXApp and xAuthType
    if (platformId === 'x_twitter') {
      console.log('[XConfigModal] Validating X config:', { useCustomXApp, xAuthType, apiFormData });
      
      if (useCustomXApp) {
        // Custom app mode - validate based on auth_type
        const missingFields = [];
        if (!apiFormData.client_id?.trim()) {
          missingFields.push('Client ID');
        }
        if (xAuthType === 'confidential' && !apiFormData.client_secret?.trim()) {
          missingFields.push('Client Secret');
        }
        
        if (missingFields.length > 0) {
          toast.error(`Champs requis manquants : ${missingFields.join(', ')}`);
          // Auto-expand advanced section to show the fields
          setActiveSection('technical');
          return;
        }
      }
      // If not useCustomXApp, no validation needed - will use env defaults
    } else {
      // Standard validation for other platforms
      const missingFields = platform.apiFields?.filter(f => !apiFormData[f.name]?.trim());
      if (missingFields?.length > 0) {
        const fieldNames = missingFields.map(f => f.label).join(', ');
        toast.error(`Champs requis manquants : ${fieldNames}`);
        return;
      }
      // P0 iter 120: Brevo also requires a target list selection.
      if (platformId === 'brevo' && !apiFormData.target_list_id) {
        toast.error('Sélectionnez une liste cible Brevo avant d\'enregistrer.');
        return;
      }
    }
    
    setConnecting(true);
    try {
      // Special handling for Brevo - needs to save credentials via dedicated endpoint
      if (platformId === 'brevo') {
        console.log('Saving Brevo credentials:', {
          api_key: apiFormData.api_key ? `${apiFormData.api_key.substring(0, 10)}...` : 'MISSING',
          sender_email: apiFormData.sender_email,
          sender_name: apiFormData.sender_name
        });
        
        // Save Brevo credentials securely - always enable when saving
        const brevoResponse = await apiClient.post('/integrations/brevo/credentials', {
          api_key: apiFormData.api_key,
          sender_email: apiFormData.sender_email,
          sender_name: apiFormData.sender_name,
          is_enabled: true,  // Always enable when saving credentials
          auto_send_enabled: false,
          // P0 iter 120: target list selection
          target_list_id: apiFormData.target_list_id || null,
        });
        
        console.log('Brevo save response:', brevoResponse.data);
        
        if (!brevoResponse.data?.success) {
          throw new Error(brevoResponse.data?.message || 'Échec de la sauvegarde des credentials');
        }
      }
      
      // Special handling for Telegram - needs to save credentials via dedicated endpoint
      if (platformId === 'telegram') {
        // P0 UX: Validate fields before saving
        const tokenError = validateTelegramFields('bot_token', apiFormData.bot_token);
        const chatError = validateTelegramFields('chat_id', apiFormData.chat_id);
        
        if (tokenError) {
          throw new Error(`Bot Token: ${tokenError}`);
        }
        if (chatError) {
          throw new Error(`Chat ID: ${chatError}`);
        }
        
        console.log('Saving Telegram credentials:', {
          bot_token: apiFormData.bot_token ? `${apiFormData.bot_token.substring(0, 10)}...` : 'MISSING',
          chat_id: apiFormData.chat_id
        });
        
        // P0 UX: Force parse_mode to HTML - user doesn't need to configure this
        const telegramResponse = await apiClient.post('/integrations/telegram', {
          bot_token: apiFormData.bot_token,
          chat_id: apiFormData.chat_id,
          parse_mode: 'HTML', // P0 UX: Always use HTML, no user input needed
          disable_web_page_preview: false,
          is_enabled: true
        });
        
        console.log('Telegram save response:', telegramResponse.data);
        
        if (!telegramResponse.data?.success) {
          throw new Error(telegramResponse.data?.message || 'Échec de la sauvegarde des credentials Telegram');
        }
        
        // Show bot username if available
        if (telegramResponse.data?.bot_username) {
          toast.success(`Bot @${telegramResponse.data.bot_username} configuré !`);
        }
      }
      
      // P0 FIX: Special handling for X (Twitter) - Direct OAuth 2.0 flow (NO client_id/secret input)
      // Client ID is configured on server via X_CLIENT_ID env var
      // P0 RESTORE: Handle X (Twitter) with support for custom credentials
      if (platformId === 'x_twitter') {
        console.log('[X_OAUTH] Starting OAuth flow, useCustomApp:', useCustomXApp);
        
        // Step 1: If using custom app, save credentials first
        if (useCustomXApp && apiFormData.client_id) {
          console.log('[X_OAUTH] Saving custom app credentials...');
          const saveResp = await apiClient.post('/integrations/x/config', {
            project_id: projectId,
            client_id: apiFormData.client_id,
            client_secret: apiFormData.client_secret || null,
            auth_type: xAuthType
          });
          
          if (!saveResp.data?.ok) {
            throw new Error(saveResp.data?.message || 'Échec de la sauvegarde des credentials');
          }
          
          toast.success('Credentials X sauvegardés !');
        }
        
        // Step 2: Start OAuth 2.0 flow
        toast.info('Redirection vers X pour autorisation...');
        const oauthResponse = await apiClient.get(`/auth/x/start?project_id=${projectId}`);
        
        if (oauthResponse.data?.authorization_url) {
          window.location.href = oauthResponse.data.authorization_url;
          return; // Don't close modal, we're redirecting
        } else if (oauthResponse.data?.error_code === 'X_NOT_CONFIGURED') {
          throw new Error('X OAuth n\'est pas configuré. Activez "Utiliser mon app X" et entrez vos credentials.');
        } else {
          throw new Error(oauthResponse.data?.message || 'URL d\'autorisation non reçue');
        }
      }
      
      // Mark channel as connected in project
      await apiClient.put(`/projects/${projectId}/channels/${platformId}`, {
        enabled: true,
        connected: true
      });
      
      toast.success(`${platform.name} configuré ! Utilisez le bouton Test pour vérifier la connexion.`);
      onConnect?.(platformId);
      onClose();
    } catch (error) {
      console.error('Connect error:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erreur lors de la configuration';
      toast.error(errorMsg);
    } finally {
      setConnecting(false);
    }
  };
  
  // P0 RESTORE: Handler to clear custom X credentials
  const handleClearXCredentials = async () => {
    try {
      const resp = await apiClient.post('/integrations/x/config', {
        project_id: projectId,
        clear_credentials: true
      });
      if (resp.data?.ok) {
        setUseCustomXApp(false);
        setApiFormData({});
        setXConfigStatus(null);
        toast.success('Credentials X supprimés. L\'app par défaut sera utilisée.');
      }
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="modal-backdrop"
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl ios-animate-scale-in flex flex-col"
        style={{ backgroundColor: 'hsl(var(--color-surface))' }}
        data-testid={`platform-modal-${platformId}`}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-5"
          style={{ borderBottom: '1px solid hsl(var(--color-border))' }}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: platform.gradient }}
            >
              <PlatformLogo platformId={platformId} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-semantic">Configurer {platform.name}</h2>
              <p className="text-sm text-semantic-muted">
                {platform.type === 'oauth' ? 'Connexion OAuth' : 'Connexion API Key'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors hover:bg-semantic-surface-2"
            data-testid="close-platform-modal"
          >
            <X className="w-5 h-5 text-semantic-muted" />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Section Tabs */}
          <div className="flex gap-2 flex-wrap">
            {['prerequisites', 'technical', 'errors'].map(section => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all',
                  activeSection === section
                    ? 'bg-semantic-primary text-white'
                    : 'bg-semantic-surface-2 text-semantic-2 hover:bg-semantic-surface-3'
                )}
                data-testid={`section-tab-${section}`}
              >
                {section === 'prerequisites' && '📋 Prérequis'}
                {section === 'technical' && '🔧 Technique'}
                {section === 'errors' && '⚠️ Erreurs'}
              </button>
            ))}
          </div>
          
          {/* Prerequisites Section */}
          {activeSection === 'prerequisites' && (
            <div className="space-y-4" data-testid="section-prerequisites">
              <h3 className="font-medium text-semantic flex items-center gap-2">
                <Info className="w-4 h-4" />
                Avant de commencer
              </h3>
              <ul className="space-y-3">
                {platform.prerequisites?.map((prereq, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ 
                        backgroundColor: 'hsl(var(--color-primary) / 0.1)',
                        color: 'hsl(var(--color-primary))'
                      }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-sm text-semantic-2">{prereq}</p>
                  </li>
                ))}
              </ul>
              
              {/* P0 FIX: X (Twitter) credentials form - ALWAYS visible in prerequisites */}
              {platformId === 'x_twitter' && (
                <div className="space-y-4 pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
                  {/* Connection Status */}
                  {isConnected && (
                    <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 mb-4">
                      <p className="text-sm font-medium text-green-600 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Compte X connecté {xConfigStatus?.x_username && `(@${xConfigStatus.x_username})`}
                      </p>
                    </div>
                  )}
                  
                  {/* Loading state */}
                  {xConfigLoading && (
                    <div className="flex items-center gap-2 text-sm text-semantic-muted">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Chargement de la configuration...
                    </div>
                  )}
                  
                  {/* P0 FIX: Show credentials required banner if no env default */}
                  {xCredentialsRequired && !xConfigStatus?.has_custom_app && !xConfigLoading && (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <h4 className="font-medium text-amber-600 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Configuration requise
                      </h4>
                      <p className="text-sm text-semantic-muted">
                        Vous devez configurer vos propres credentials X Developer pour connecter votre compte.
                      </p>
                    </div>
                  )}
                  
                  {/* Mode selector: Default (env) or Custom App */}
                  {!xCredentialsRequired && !xConfigLoading && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-semantic-surface-secondary">
                      <div>
                        <label className="text-sm font-medium text-semantic flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Utiliser mon app X Developer
                        </label>
                        <p className="text-xs text-semantic-muted mt-0.5">
                          {useCustomXApp ? 'Vos propres credentials' : 'Connexion simplifiée par défaut'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setUseCustomXApp(!useCustomXApp)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          useCustomXApp ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        data-testid="use-custom-x-app-toggle"
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          useCustomXApp ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  )}
                  
                  {/* P0 FIX: X (Twitter) REFACTORED - 2 modes: Public (OAuth1 images) OR Confidential (OAuth2 text) */}
                  {(useCustomXApp || xCredentialsRequired) && !xConfigLoading && (
                    <div className="space-y-4 p-4 rounded-lg border border-semantic-border bg-semantic-surface-secondary" data-testid="x-credentials-form">
                      <h4 className="font-medium text-semantic">Configuration X Developer</h4>
                      
                      {/* Auth type selector - Now with clear purpose labels */}
                      <div>
                        <label className="block text-sm font-medium text-semantic mb-2">
                          Type d'application X
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* PUBLIC (PKCE) = OAuth 1.0a for IMAGES */}
                          <button
                            type="button"
                            onClick={() => setXAuthType('public_pkce')}
                            className={cn(
                              'p-4 rounded-xl border-2 text-left transition-all relative',
                              xAuthType === 'public_pkce' 
                                ? 'border-orange-500 bg-orange-500/10' 
                                : 'border-semantic-border hover:bg-semantic-surface-3'
                            )}
                            data-testid="x-auth-type-public"
                          >
                            {/* Badge OAuth 1.0a status */}
                            {xAuthType === 'public_pkce' && xConfigStatus?.has_oauth1_fallback && (
                              <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
                                🔑 Actif
                              </span>
                            )}
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">🖼️</span>
                              <span className="font-semibold text-semantic">Public (PKCE)</span>
                            </div>
                            <p className="text-xs text-orange-600 font-medium mb-1">
                              Upload d'images via OAuth 1.0a
                            </p>
                            <p className="text-xs text-semantic-muted">
                              API Key + API Secret uniquement
                            </p>
                          </button>
                          
                          {/* CONFIDENTIAL = OAuth 2.0 for TEXT */}
                          <button
                            type="button"
                            onClick={() => setXAuthType('confidential')}
                            className={cn(
                              'p-4 rounded-xl border-2 text-left transition-all',
                              xAuthType === 'confidential' 
                                ? 'border-blue-500 bg-blue-500/10' 
                                : 'border-semantic-border hover:bg-semantic-surface-3'
                            )}
                            data-testid="x-auth-type-confidential"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">📝</span>
                              <span className="font-semibold text-semantic">Confidential</span>
                            </div>
                            <p className="text-xs text-blue-600 font-medium mb-1">
                              Publication texte via OAuth 2.0
                            </p>
                            <p className="text-xs text-semantic-muted">
                              Client ID + Client Secret
                            </p>
                          </button>
                        </div>
                      </div>
                      
                      {/* ========================================
                          MODE PUBLIC (PKCE) = OAuth 1.0a IMAGES
                          ======================================== */}
                      {xAuthType === 'public_pkce' && (
                        <div className="space-y-4 p-4 rounded-lg border border-dashed border-orange-500/30 bg-orange-500/5" data-testid="oauth1-form-section">
                          {/* Status badge */}
                          <div className={cn(
                            "p-3 rounded-lg flex items-center gap-3",
                            xConfigStatus?.has_oauth1_fallback 
                              ? "bg-green-500/10 border border-green-500/20" 
                              : "bg-amber-500/10 border border-amber-500/20"
                          )}>
                            {xConfigStatus?.has_oauth1_fallback ? (
                              <>
                                <Check className="w-5 h-5 text-green-600" />
                                <div>
                                  <p className="font-medium text-green-700 text-sm">🔑 OAuth 1.0a actif</p>
                                  <p className="text-xs text-semantic-muted">
                                    Images activées{xConfigStatus?.oauth1_screen_name && ` (@${xConfigStatus.oauth1_screen_name})`}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                                <div>
                                  <p className="font-medium text-amber-700 text-sm">⚠️ Images non actives</p>
                                  <p className="text-xs text-semantic-muted">Configurez OAuth 1.0a ci-dessous</p>
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* Explanation */}
                          <p className="text-xs text-semantic-muted">
                            L'API X sur plan Basic/Free bloque l'upload d'images via OAuth 2.0. 
                            OAuth 1.0a permet de contourner cette limitation.
                          </p>
                          
                          {/* API Key input */}
                          <div>
                            <label className="block text-sm font-medium text-semantic mb-1.5">
                              API Key (Consumer Key) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={apiFormData.oauth1_consumer_key || ''}
                              onChange={(e) => setApiFormData(prev => ({ ...prev, oauth1_consumer_key: e.target.value }))}
                              placeholder="Votre API Key"
                              className="w-full input-semantic"
                              data-testid="oauth1-api-key-input"
                            />
                          </div>
                          
                          {/* API Secret input */}
                          <div>
                            <label className="block text-sm font-medium text-semantic mb-1.5">
                              API Key Secret (Consumer Secret) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="password"
                              value={apiFormData.oauth1_consumer_secret || ''}
                              onChange={(e) => setApiFormData(prev => ({ ...prev, oauth1_consumer_secret: e.target.value }))}
                              placeholder="Votre API Key Secret"
                              className="w-full input-semantic"
                              data-testid="oauth1-api-secret-input"
                            />
                          </div>
                          
                          {/* Callback URL OAuth 1.0a */}
                          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                            <label className="block text-sm font-medium text-amber-700 mb-1.5">
                              ⚠️ Callback URL OAuth 1.0a (à ajouter dans X Developer Portal)
                            </label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs bg-white/50 p-2 rounded font-mono break-all">
                                https://ai-marketing-engine.com/api/integrations/x/oauth1/callback
                              </code>
                              <button
                                type="button"
                                onClick={() => copy('https://ai-marketing-engine.com/api/integrations/x/oauth1/callback', 'OAuth1 Callback')}
                                className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 flex-shrink-0"
                              >
                                {copied === 'OAuth1 Callback' ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4 text-amber-700" />
                                )}
                              </button>
                            </div>
                          </div>
                          
                          {/* Connect button OAuth 1.0a */}
                          <PillButton
                            variant="primary"
                            className="w-full bg-orange-500 hover:bg-orange-600"
                            onClick={async () => {
                              if (!apiFormData.oauth1_consumer_key || !apiFormData.oauth1_consumer_secret) {
                                toast.error('API Key et API Secret requis');
                                return;
                              }
                              try {
                                const response = await apiClient.post('/integrations/x/oauth1/start', {
                                  project_id: projectId,
                                  consumer_key: apiFormData.oauth1_consumer_key,
                                  consumer_secret: apiFormData.oauth1_consumer_secret
                                });
                                if (response.data.success && response.data.authorization_url) {
                                  toast.success('Redirection vers X pour autorisation...');
                                  window.location.href = response.data.authorization_url;
                                } else {
                                  toast.error(response.data.detail || 'Erreur lors du démarrage');
                                }
                              } catch (error) {
                                const errorData = error.response?.data;
                                if (errorData?.error_code === 'OAUTH1_CALLBACK_NOT_APPROVED') {
                                  toast.error('Callback URL non approuvée par X. Ajoutez-la dans le Developer Portal.');
                                } else {
                                  toast.error(errorData?.detail || error.message);
                                }
                              }
                            }}
                            icon={<Key className="w-4 h-4" />}
                            data-testid="oauth1-connect-btn"
                          >
                            Connecter Images (OAuth 1.0a)
                          </PillButton>
                          
                          {/* Delete config if configured */}
                          {xConfigStatus?.has_oauth1_fallback && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!confirm('Supprimer la configuration OAuth 1.0a ?')) return;
                                try {
                                  await apiClient.delete(`/integrations/x/oauth1-fallback?project_id=${projectId}`);
                                  toast.success('Configuration OAuth 1.0a supprimée');
                                  const resp = await apiClient.get(`/integrations/x/config?project_id=${projectId}`);
                                  setXConfigStatus(resp.data);
                                } catch (error) {
                                  toast.error('Erreur lors de la suppression');
                                }
                              }}
                              className="w-full text-center text-xs text-red-600 hover:underline py-1"
                            >
                              Supprimer la configuration OAuth 1.0a
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* ========================================
                          MODE CONFIDENTIAL = OAuth 2.0 TEXT
                          ======================================== */}
                      {xAuthType === 'confidential' && (
                        <div className="space-y-4 p-4 rounded-lg border border-dashed border-blue-500/30 bg-blue-500/5" data-testid="oauth2-form-section">
                          {/* Connection status */}
                          {isConnected && (
                            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                              <Check className="w-5 h-5 text-green-600" />
                              <div>
                                <p className="font-medium text-green-700 text-sm">✅ OAuth 2.0 connecté</p>
                                <p className="text-xs text-semantic-muted">
                                  Publication texte activée{xConfigStatus?.x_username && ` (@${xConfigStatus.x_username})`}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Warning if OAuth1 not configured */}
                          {!xConfigStatus?.has_oauth1_fallback && (
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-3">
                              <AlertCircle className="w-5 h-5 text-amber-600" />
                              <div>
                                <p className="font-medium text-amber-700 text-sm">⚠️ Images non actives</p>
                                <p className="text-xs text-semantic-muted">
                                  Passez en mode "Public (PKCE)" pour activer l'upload d'images
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Existing credentials status */}
                          {xConfigStatus?.has_custom_app && (
                            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                              <p className="text-sm text-blue-700 flex items-center gap-2">
                                <Check className="w-4 h-4" />
                                Client ID configuré: {xConfigStatus.client_id_preview}
                              </p>
                              <button
                                type="button"
                                onClick={handleClearXCredentials}
                                className="mt-2 text-xs text-red-600 hover:underline"
                              >
                                Supprimer et revenir à l'app par défaut
                              </button>
                            </div>
                          )}
                          
                          {/* Client ID input */}
                          <div>
                            <label className="block text-sm font-medium text-semantic mb-1.5">
                              Client ID (OAuth 2.0) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={apiFormData.client_id || ''}
                              onChange={(e) => setApiFormData(prev => ({ ...prev, client_id: e.target.value }))}
                              placeholder="Votre Client ID OAuth 2.0"
                              className="w-full input-semantic"
                              data-testid="x-client-id-input"
                            />
                          </div>
                          
                          {/* Client Secret input */}
                          <div>
                            <label className="block text-sm font-medium text-semantic mb-1.5">
                              Client Secret (OAuth 2.0) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="password"
                              value={apiFormData.client_secret || ''}
                              onChange={(e) => setApiFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                              placeholder="Votre Client Secret"
                              className="w-full input-semantic"
                              data-testid="x-client-secret-input"
                            />
                          </div>
                          
                          {/* Callback URL OAuth 2.0 */}
                          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <label className="block text-sm font-medium text-blue-700 mb-1.5">
                              Callback URL OAuth 2.0 (à ajouter dans X Developer Portal)
                            </label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs bg-white/50 p-2 rounded font-mono break-all">
                                https://ai-marketing-engine.com/api/auth/x/callback
                              </code>
                              <button
                                type="button"
                                onClick={() => copy('https://ai-marketing-engine.com/api/auth/x/callback', 'OAuth2 Callback')}
                                className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 flex-shrink-0"
                              >
                                {copied === 'OAuth2 Callback' ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4 text-blue-700" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Developer portal link */}
                      <a 
                        href="https://developer.twitter.com/en/portal/dashboard" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Ouvrir X Developer Portal
                      </a>
                    </div>
                  )}
                  
                  {/* Default mode info - when NOT using custom app */}
                  {!useCustomXApp && !xCredentialsRequired && !xConfigLoading && (
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                      <h4 className="font-medium text-blue-600 mb-2 flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Connexion simplifiée
                      </h4>
                      <p className="text-sm text-semantic-muted">
                        Cliquez sur "Connecter X" pour autoriser AI Marketing Engine à publier sur votre compte.
                        Aucune configuration technique requise.
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* API Key Form (for blog, brevo - NOT telegram or x_twitter) */}
              {platform.type === 'api_key' && platform.apiFields && platformId !== 'x_twitter' && platformId !== 'telegram' && (
                <div className="space-y-4 pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
                  <h4 className="font-medium text-semantic">Informations de connexion</h4>
                  
                  {/* Callback URL for X OAuth 2.0 */}
                  {platform.needsCallback && (
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <p className="text-sm font-medium text-amber-600 mb-2">
                        ⚠️ Ajoutez cette URL de Callback dans X Developer Portal :
                      </p>
                      
                      {/* Production URL - Main */}
                      <div className="mb-3">
                        <p className="text-xs font-medium text-semantic-muted mb-1">
                          🚀 Production (ai-marketing-engine.com)
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value="https://ai-marketing-engine.com/api/auth/x/callback"
                            readOnly
                            className="flex-1 input-semantic text-xs font-mono bg-green-500/5 border-green-500/20"
                          />
                          <button
                            onClick={() => copy('https://ai-marketing-engine.com/api/auth/x/callback', 'X Callback Prod')}
                            className="p-2 rounded-lg transition-colors bg-green-500/10 hover:bg-green-500/20"
                          >
                            {copied === 'X Callback Prod' ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-green-600" />
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Preview URL - Secondary */}
                      {callbackUrls && callbackUrls.preview !== 'https://ai-marketing-engine.com/api/auth/x/callback' && (
                        <div>
                          <p className="text-xs font-medium text-semantic-muted mb-1">
                            🧪 Preview/Test
                          </p>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={callbackUrls.preview}
                              readOnly
                              className="flex-1 input-semantic text-xs font-mono"
                            />
                            <button
                              onClick={() => copy(callbackUrls.preview, 'X Callback Preview')}
                              className="p-2 rounded-lg transition-colors bg-semantic-surface-2 hover:bg-semantic-surface-3"
                            >
                              {copied === 'X Callback Preview' ? (
                                <Check className="w-4 h-4 text-semantic-success" />
                              ) : (
                                <Copy className="w-4 h-4 text-semantic-muted" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-amber-500/80 mt-3">
                        📍 Dans X Developer Portal → User authentication settings → Callback URI
                      </p>
                    </div>
                  )}
                  
                  {platform.apiFields.map(field => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-semantic mb-1.5">
                        {field.label}
                      </label>
                      <input
                        type={field.type || 'text'}
                        value={apiFormData[field.name] || ''}
                        onChange={(e) => setApiFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={field.placeholder}
                        className="w-full input-semantic"
                        data-testid={`api-field-${field.name}`}
                      />
                    </div>
                  ))}

                  {/* P0 iter 120: Brevo target list dropdown.
                      Visible once Brevo credentials have been saved (we need
                      a valid api_key to fetch the lists). */}
                  {platformId === 'brevo' && (
                    <BrevoTargetListSelector
                      value={apiFormData.target_list_id}
                      onChange={(id) => setApiFormData(prev => ({ ...prev, target_list_id: id }))}
                    />
                  )}
                </div>
              )}
              
              {/* ================================================================ */}
              {/* P0 UX: TELEGRAM SIMPLIFIED INTERFACE                            */}
              {/* ================================================================ */}
              {platformId === 'telegram' && (
                <div className="space-y-5 pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
                  <h4 className="font-medium text-semantic flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Informations requises
                  </h4>
                  
                  {/* Bot Token Field */}
                  <div>
                    <label className="block text-sm font-medium text-semantic mb-1.5">
                      Bot Token <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={apiFormData.bot_token || ''}
                        onChange={(e) => {
                          setApiFormData(prev => ({ ...prev, bot_token: e.target.value }));
                          setTelegramValidation(prev => ({
                            ...prev,
                            bot_token: validateTelegramFields('bot_token', e.target.value)
                          }));
                          setTelegramTestResult(null);
                        }}
                        placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                        className={cn(
                          "flex-1 input-semantic font-mono text-sm",
                          telegramValidation.bot_token && "border-red-500"
                        )}
                        data-testid="telegram-bot-token"
                      />
                      <button
                        type="button"
                        onClick={handleTelegramTestConnection}
                        disabled={telegramTestLoading || !apiFormData.bot_token}
                        className={cn(
                          "px-4 py-2 rounded-xl text-sm font-medium transition-all",
                          "bg-[#0088cc]/10 text-[#0088cc] hover:bg-[#0088cc]/20",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                          "flex items-center gap-2"
                        )}
                      >
                        {telegramTestLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : telegramTestResult?.success ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Settings className="w-4 h-4" />
                        )}
                        Tester
                      </button>
                    </div>
                    {telegramValidation.bot_token && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {telegramValidation.bot_token}
                      </p>
                    )}
                    {telegramTestResult && (
                      <p className={cn(
                        "text-xs mt-1 flex items-center gap-1",
                        telegramTestResult.success ? "text-green-600" : "text-red-500"
                      )}>
                        {telegramTestResult.success ? (
                          <>
                            <Check className="w-3 h-3" />
                            Bot @{telegramTestResult.bot_username} connecté
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            {telegramTestResult.error}
                          </>
                        )}
                      </p>
                    )}
                    <p className="text-xs text-semantic-muted mt-1">
                      Obtenez-le via <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-[#0088cc] hover:underline">@BotFather</a> sur Telegram
                    </p>
                  </div>
                  
                  {/* Chat ID Field */}
                  <div>
                    <label className="block text-sm font-medium text-semantic mb-1.5">
                      Chat ID / Canal <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={apiFormData.chat_id || ''}
                        onChange={(e) => {
                          setApiFormData(prev => ({ ...prev, chat_id: e.target.value }));
                          setTelegramValidation(prev => ({
                            ...prev,
                            chat_id: validateTelegramFields('chat_id', e.target.value)
                          }));
                        }}
                        placeholder="@moncanal ou -1001234567890"
                        className={cn(
                          "flex-1 input-semantic text-sm",
                          telegramValidation.chat_id && "border-red-500"
                        )}
                        data-testid="telegram-chat-id"
                      />
                      <button
                        type="button"
                        onClick={handleTelegramDetectChatId}
                        disabled={telegramDetectLoading || !apiFormData.bot_token || !telegramTestResult?.success}
                        className={cn(
                          "px-4 py-2 rounded-xl text-sm font-medium transition-all",
                          "bg-[#0088cc]/10 text-[#0088cc] hover:bg-[#0088cc]/20",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                          "flex items-center gap-2 whitespace-nowrap"
                        )}
                        title={!telegramTestResult?.success ? "Testez d'abord le Bot Token" : "Détecter automatiquement"}
                      >
                        {telegramDetectLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Globe className="w-4 h-4" />
                        )}
                        Détecter
                      </button>
                    </div>
                    {telegramValidation.chat_id && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {telegramValidation.chat_id}
                      </p>
                    )}
                    <p className="text-xs text-semantic-muted mt-1">
                      Utilisez @username pour un canal public ou l'ID numérique pour un groupe/canal privé
                    </p>
                    
                    {/* Detected Chats Dropdown */}
                    {telegramDetectedChats.length > 0 && (
                      <div className="mt-2 p-3 rounded-xl bg-[#0088cc]/5 border border-[#0088cc]/20">
                        <p className="text-xs font-medium text-[#0088cc] mb-2">
                          Chats détectés - Cliquez pour sélectionner :
                        </p>
                        <div className="space-y-1">
                          {telegramDetectedChats.map((chat) => (
                            <button
                              key={chat.id}
                              type="button"
                              onClick={() => handleSelectDetectedChat(chat)}
                              className="w-full text-left px-3 py-2 rounded-lg bg-white/50 hover:bg-white/80 transition-colors text-sm flex items-center justify-between"
                            >
                              <span className="font-medium">{chat.title}</span>
                              <span className="text-xs text-semantic-muted">
                                {chat.type} • {chat.id}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Help Box */}
                  <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
                    <p className="text-xs text-semantic-muted">
                      <strong className="text-semantic">Pour détecter le Chat ID :</strong>
                      <br />
                      1. Ajoutez votre bot comme administrateur du canal/groupe
                      <br />
                      2. Envoyez un message dans le canal ou écrivez au bot
                      <br />
                      3. Cliquez sur "Détecter" pour trouver automatiquement le Chat ID
                    </p>
                  </div>
                </div>
              )}
              
              {/* ================================================================ */}
              {/* PINTEREST CONFIGURATION INTERFACE                              */}
              {/* ================================================================ */}
              {platformId === 'pinterest' && (
                <PinterestConfigSection
                  apiFormData={apiFormData}
                  setApiFormData={setApiFormData}
                  projectId={projectId}
                  onConnectionChange={setIsConnected}
                />
              )}
              
              {/* P1 FIX: Facebook/Instagram - Meta App Configuration */}
              {(platformId === 'facebook' || platformId === 'instagram') && (
                <div className="pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
                  <MetaAppConfig
                    platform={platformId}
                    projectId={projectId}
                    onConfigSaved={() => {
                      toast.success('Configuration Meta mise à jour');
                    }}
                    onStatusChange={(metaStatus) => {
                      console.log('[PlatformModal] Meta status:', metaStatus);
                      if (metaStatus.hasOAuth && metaStatus.canPublish) {
                        setIsConnected(true);
                      }
                    }}
                    showOAuthButton={true}
                    oauthButtonLabel={platformId === 'facebook' ? 'Connecter Facebook via OAuth' : 'Connecter Instagram via OAuth'}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Technical Section */}
          {activeSection === 'technical' && (
            <div className="space-y-4" data-testid="section-technical">
              {/* P0 FIX: X Twitter refactored - 2 clear modes */}
              {platform.type === 'oauth_hybrid' && (
                <div className="space-y-4">
                  {/* Connection Status */}
                  {isConnected && (
                    <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                      <p className="text-sm font-medium text-green-600 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Compte X connecté
                      </p>
                    </div>
                  )}
                  
                  {/* Advanced: Use my X App toggle */}
                  <div className="pt-2">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label className="text-sm font-medium text-semantic flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Avancé: Utiliser mon app X
                        </label>
                        <p className="text-xs text-semantic-muted">
                          Connectez avec vos propres credentials X Developer
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setUseCustomXApp(!useCustomXApp)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          useCustomXApp ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        data-testid="use-custom-x-app-toggle"
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          useCustomXApp ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    
                    {/* Default mode message */}
                    {!useCustomXApp && (
                      <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                        <h4 className="font-medium text-blue-600 mb-2 flex items-center gap-2">
                          <Link2 className="w-4 h-4" />
                          Connexion simplifiée
                        </h4>
                        <p className="text-sm text-semantic-muted">
                          Cliquez sur "Connecter" pour autoriser AI Marketing Engine à publier sur votre compte X.
                        </p>
                      </div>
                    )}
                    
                    {/* Custom app form - P0 FIX: 2 clear modes */}
                    {useCustomXApp && (
                      <div className="space-y-4">
                        {/* Mode selector cards */}
                        <div>
                          <label className="block text-sm font-medium text-semantic mb-2">
                            Type d'application X
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* PUBLIC (PKCE) = OAuth 1.0a for IMAGES */}
                            <button
                              type="button"
                              onClick={() => setXAuthType('public_pkce')}
                              className={cn(
                                'p-4 rounded-xl border-2 text-left transition-all relative',
                                xAuthType === 'public_pkce' 
                                  ? 'border-orange-500 bg-orange-500/10' 
                                  : 'border-semantic-border hover:bg-semantic-surface-3'
                              )}
                              data-testid="x-auth-type-public-tech"
                            >
                              {xAuthType === 'public_pkce' && xConfigStatus?.has_oauth1_fallback && (
                                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
                                  🔑 Actif
                                </span>
                              )}
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">🖼️</span>
                                <span className="font-semibold text-semantic">Public (PKCE)</span>
                              </div>
                              <p className="text-xs text-orange-600 font-medium mb-1">
                                Upload d'images via OAuth 1.0a
                              </p>
                              <p className="text-xs text-semantic-muted">
                                API Key + API Secret uniquement
                              </p>
                            </button>
                            
                            {/* CONFIDENTIAL = OAuth 2.0 for TEXT */}
                            <button
                              type="button"
                              onClick={() => setXAuthType('confidential')}
                              className={cn(
                                'p-4 rounded-xl border-2 text-left transition-all',
                                xAuthType === 'confidential' 
                                  ? 'border-blue-500 bg-blue-500/10' 
                                  : 'border-semantic-border hover:bg-semantic-surface-3'
                              )}
                              data-testid="x-auth-type-confidential-tech"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">📝</span>
                                <span className="font-semibold text-semantic">Confidential</span>
                              </div>
                              <p className="text-xs text-blue-600 font-medium mb-1">
                                Publication texte via OAuth 2.0
                              </p>
                              <p className="text-xs text-semantic-muted">
                                Client ID + Client Secret
                              </p>
                            </button>
                          </div>
                        </div>
                        
                        {/* ========================================
                            MODE PUBLIC (PKCE) = OAuth 1.0a IMAGES
                            ======================================== */}
                        {xAuthType === 'public_pkce' && (
                          <div className="space-y-4 p-4 rounded-lg border border-dashed border-orange-500/30 bg-orange-500/5" data-testid="oauth1-form-section-tech">
                            {/* Status badge */}
                            <div className={cn(
                              "p-3 rounded-lg flex items-center gap-3",
                              xConfigStatus?.has_oauth1_fallback 
                                ? "bg-green-500/10 border border-green-500/20" 
                                : "bg-amber-500/10 border border-amber-500/20"
                            )}>
                              {xConfigStatus?.has_oauth1_fallback ? (
                                <>
                                  <Check className="w-5 h-5 text-green-600" />
                                  <div>
                                    <p className="font-medium text-green-700 text-sm">🔑 OAuth 1.0a actif</p>
                                    <p className="text-xs text-semantic-muted">
                                      Images activées{xConfigStatus?.oauth1_screen_name && ` (@${xConfigStatus.oauth1_screen_name})`}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-5 h-5 text-amber-600" />
                                  <div>
                                    <p className="font-medium text-amber-700 text-sm">⚠️ Images non actives</p>
                                    <p className="text-xs text-semantic-muted">Configurez OAuth 1.0a ci-dessous</p>
                                  </div>
                                </>
                              )}
                            </div>
                            
                            {/* API Key input */}
                            <div>
                              <label className="block text-sm font-medium text-semantic mb-1.5">
                                API Key (Consumer Key) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={apiFormData.oauth1_consumer_key || ''}
                                onChange={(e) => setApiFormData(prev => ({ ...prev, oauth1_consumer_key: e.target.value }))}
                                placeholder="Votre API Key"
                                className="w-full input-semantic"
                                data-testid="oauth1-api-key-input-tech"
                              />
                            </div>
                            
                            {/* API Secret input */}
                            <div>
                              <label className="block text-sm font-medium text-semantic mb-1.5">
                                API Key Secret (Consumer Secret) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={apiFormData.oauth1_consumer_secret || ''}
                                onChange={(e) => setApiFormData(prev => ({ ...prev, oauth1_consumer_secret: e.target.value }))}
                                placeholder="Votre API Key Secret"
                                className="w-full input-semantic"
                                data-testid="oauth1-api-secret-input-tech"
                              />
                            </div>
                            
                            {/* Callback URL OAuth 1.0a */}
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                              <label className="block text-sm font-medium text-amber-700 mb-1.5">
                                ⚠️ Callback URL OAuth 1.0a (à ajouter dans X Developer Portal)
                              </label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-white/50 p-2 rounded font-mono break-all">
                                  https://ai-marketing-engine.com/api/integrations/x/oauth1/callback
                                </code>
                                <button
                                  type="button"
                                  onClick={() => copy('https://ai-marketing-engine.com/api/integrations/x/oauth1/callback', 'OAuth1 Callback Tech')}
                                  className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 flex-shrink-0"
                                >
                                  {copied === 'OAuth1 Callback Tech' ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-amber-700" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* ========================================
                            MODE CONFIDENTIAL = OAuth 2.0 TEXT
                            ======================================== */}
                        {xAuthType === 'confidential' && (
                          <div className="space-y-4 p-4 rounded-lg border border-dashed border-blue-500/30 bg-blue-500/5" data-testid="oauth2-form-section-tech">
                            {/* Client ID input */}
                            <div>
                              <label className="block text-sm font-medium text-semantic mb-1.5">
                                Client ID (OAuth 2.0) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={apiFormData.client_id || ''}
                                onChange={(e) => setApiFormData(prev => ({ ...prev, client_id: e.target.value }))}
                                placeholder="Votre Client ID OAuth 2.0"
                                className="w-full input-semantic"
                                data-testid="x-client-id-input-tech"
                              />
                            </div>
                            
                            {/* Client Secret input */}
                            <div>
                              <label className="block text-sm font-medium text-semantic mb-1.5">
                                Client Secret (OAuth 2.0) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={apiFormData.client_secret || ''}
                                onChange={(e) => setApiFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                                placeholder="Votre Client Secret"
                                className="w-full input-semantic"
                                data-testid="x-client-secret-input-tech"
                              />
                            </div>
                            
                            {/* Callback URL OAuth 2.0 */}
                            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                              <label className="block text-sm font-medium text-blue-700 mb-1.5">
                                Callback URL OAuth 2.0 (à ajouter dans X Developer Portal)
                              </label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-white/50 p-2 rounded font-mono break-all">
                                  https://ai-marketing-engine.com/api/auth/x/callback
                                </code>
                                <button
                                  type="button"
                                  onClick={() => copy('https://ai-marketing-engine.com/api/auth/x/callback', 'OAuth2 Callback Tech')}
                                  className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 flex-shrink-0"
                                >
                                  {copied === 'OAuth2 Callback Tech' ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-blue-700" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Developer portal link */}
                        <a 
                          href="https://developer.twitter.com/en/portal/dashboard" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ouvrir X Developer Portal
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {platform.type === 'oauth' && callbackUrls && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-semantic">
                    <Link2 className="w-4 h-4 inline mr-1" />
                    Callback URLs / Redirect URIs
                  </label>
                  
                  {/* Preview URL */}
                  <div>
                    <span className="block text-xs font-medium text-semantic-muted mb-1">
                      🔧 Preview (développement)
                    </span>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={callbackUrls.preview}
                        readOnly
                        className="flex-1 input-semantic text-xs font-mono"
                        style={{ opacity: 0.9 }}
                      />
                      <button
                        onClick={() => copy(callbackUrls.preview, 'Preview Callback URL')}
                        className="p-2 rounded-lg transition-colors bg-semantic-surface-2 hover:bg-semantic-surface-3"
                        data-testid="copy-preview-callback-url"
                      >
                        {copied === 'Preview Callback URL' ? (
                          <Check className="w-4 h-4 text-semantic-success" />
                        ) : (
                          <Copy className="w-4 h-4 text-semantic-muted" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Production URL */}
                  <div>
                    <span className="block text-xs font-medium text-semantic-muted mb-1">
                      🚀 Production
                    </span>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={callbackUrls.production}
                        readOnly
                        className="flex-1 input-semantic text-xs font-mono"
                        style={{ opacity: 0.9 }}
                      />
                      <button
                        onClick={() => copy(callbackUrls.production, 'Production Callback URL')}
                        className="p-2 rounded-lg transition-colors bg-semantic-surface-2 hover:bg-semantic-surface-3"
                        data-testid="copy-production-callback-url"
                      >
                        {copied === 'Production Callback URL' ? (
                          <Check className="w-4 h-4 text-semantic-success" />
                        ) : (
                          <Copy className="w-4 h-4 text-semantic-muted" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-semantic-muted p-2 rounded-lg" style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}>
                    💡 Ajoutez les <strong>deux URLs</strong> dans les paramètres de votre app développeur pour permettre le test en preview et la production.
                  </p>
                </div>
              )}
              
              {/* Scopes (OAuth only) */}
              {platform.scopes && (
                <div>
                  <label className="block text-sm font-medium text-semantic mb-2">
                    <Key className="w-4 h-4 inline mr-1" />
                    Permissions requises (Scopes)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {platform.scopes.map(scope => (
                      <span 
                        key={scope} 
                        className="px-2.5 py-1 rounded-lg text-xs font-mono bg-semantic-surface-2 text-semantic-2"
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* External Links */}
              <div className="flex flex-wrap gap-3 pt-2">
                {platform.developerPortalUrl && (
                  <a
                    href={platform.developerPortalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors bg-semantic-surface-2 hover:bg-semantic-surface-3"
                    style={{ color: 'hsl(var(--color-primary))' }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Portail développeur
                  </a>
                )}
                {platform.docUrl && (
                  <a
                    href={platform.docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors bg-semantic-surface-2 hover:bg-semantic-surface-3"
                    style={{ color: 'hsl(var(--color-primary))' }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Documentation API
                  </a>
                )}
              </div>
            </div>
          )}
          
          {/* Errors Section */}
          {activeSection === 'errors' && (
            <div className="space-y-4" data-testid="section-errors">
              <h3 className="font-medium text-semantic flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Erreurs fréquentes
              </h3>
              {platform.commonErrors?.length > 0 ? (
                <div className="space-y-3">
                  {platform.commonErrors.map((error, i) => (
                    <div 
                      key={i} 
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: 'hsl(var(--color-warning-bg))' }}
                    >
                      <p className="text-sm font-medium" style={{ color: 'hsl(var(--color-warning-text))' }}>
                        {error.code}
                      </p>
                      <p className="text-xs text-semantic-2 mt-1">
                        <strong>Signification :</strong> {error.meaning}
                      </p>
                      <p className="text-xs text-semantic-2">
                        <strong>Solution :</strong> {error.fix}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-semantic-muted">Aucune erreur documentée pour cette plateforme.</p>
              )}
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div 
          className="p-5 flex gap-3"
          style={{ borderTop: '1px solid hsl(var(--color-border))' }}
        >
          {isConnected && (
            <PillButton
              variant="secondary"
              onClick={handleDisconnect}
              loading={disconnecting}
              className="text-red-500 hover:bg-red-500/10"
              data-testid="disconnect-platform-btn"
            >
              Déconnecter
            </PillButton>
          )}
          <PillButton
            variant="secondary"
            className="flex-1"
            onClick={onClose}
          >
            {isConnected ? 'Fermer' : 'Annuler'}
          </PillButton>
          
          {/* P0 FIX: Contextual connect button for X Twitter */}
          {platformId === 'x_twitter' && (useCustomXApp || xCredentialsRequired) ? (
            xAuthType === 'public_pkce' ? (
              /* PUBLIC (PKCE) = OAuth 1.0a - button handled inline in form above */
              <PillButton
                variant="primary"
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                onClick={async () => {
                  if (!apiFormData.oauth1_consumer_key || !apiFormData.oauth1_consumer_secret) {
                    toast.error('API Key et API Secret requis');
                    return;
                  }
                  try {
                    const response = await apiClient.post('/integrations/x/oauth1/start', {
                      project_id: projectId,
                      consumer_key: apiFormData.oauth1_consumer_key,
                      consumer_secret: apiFormData.oauth1_consumer_secret
                    });
                    if (response.data.success && response.data.authorization_url) {
                      toast.success('Redirection vers X pour autorisation...');
                      window.location.href = response.data.authorization_url;
                    } else {
                      toast.error(response.data.detail || 'Erreur lors du démarrage');
                    }
                  } catch (error) {
                    const errorData = error.response?.data;
                    if (errorData?.error_code === 'OAUTH1_CALLBACK_NOT_APPROVED') {
                      toast.error('Callback URL non approuvée par X. Ajoutez-la dans le Developer Portal.');
                    } else {
                      toast.error(errorData?.detail || error.message);
                    }
                  }
                }}
                loading={connecting}
                icon={<Key className="w-4 h-4" />}
                data-testid="connect-oauth1-main-btn"
              >
                Connecter Images (OAuth 1.0a)
              </PillButton>
            ) : (
              /* CONFIDENTIAL = OAuth 2.0 */
              <PillButton
                variant="primary"
                className="flex-1"
                onClick={handleOAuthConnect}
                loading={connecting}
                icon={<ChevronRight className="w-4 h-4" />}
                iconPosition="right"
                data-testid="connect-oauth2-main-btn"
              >
                Connecter X (OAuth 2.0)
              </PillButton>
            )
          ) : (
            /* Default connect button for other platforms */
            <PillButton
              variant="primary"
              className="flex-1"
              onClick={platform.type === 'oauth' ? handleOAuthConnect : handleApiKeyConnect}
              loading={connecting}
              icon={<ChevronRight className="w-4 h-4" />}
              iconPosition="right"
              data-testid="connect-platform-btn"
            >
              {isConnected ? 'Reconnecter' : (
                platform.type === 'oauth' ? 'Connecter via OAuth' : 
                platform.type === 'oauth_hybrid' ? 'Connecter X' : 
                'Enregistrer'
              )}
            </PillButton>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Global function to open platform modal
 * Usage: openPlatformModal('instagram')
 */
export function openPlatformModal(platformId) {
  window.dispatchEvent(new CustomEvent('ame:open-platform-modal', {
    detail: { platform: platformId }
  }));
}
